<template>
  <div
    class="fixed inset-0 z-[60] flex items-start justify-center bg-slate-900/45 px-4 pt-[12vh] backdrop-blur-sm"
    @mousedown.self="close"
  >
    <div
      class="flex w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="go-to-fixture-title"
    >
      <div class="flex items-center gap-3 border-b border-slate-100 px-5 py-4">
        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5-5 5M6 12h12" />
          </svg>
        </div>
        <div class="min-w-0">
          <h2 id="go-to-fixture-title" class="text-sm font-semibold text-slate-900">Go to fixture</h2>
          <p class="text-xs text-slate-500">Type a fixture ID, or pick one of the recently added games</p>
        </div>
        <button
          type="button"
          class="ml-auto rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          title="Close (Esc)"
          @click="close"
        >
          <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form class="px-5 pt-4 pb-3" @submit.prevent="go()">
        <div
          class="flex items-center gap-2 rounded-xl border bg-white px-3 py-2 transition-colors"
          :class="error ? 'border-rose-300 ring-2 ring-rose-100' : 'border-slate-200 focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-100'"
        >
          <span class="text-sm font-semibold text-rose-600">#</span>
          <input
            ref="inputRef"
            v-model="fixtureId"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            spellcheck="false"
            placeholder="Fixture ID"
            class="w-full bg-transparent text-sm tabular-nums text-slate-900 placeholder-slate-400 outline-none"
            @input="onInput"
            @keydown="onKeydown"
          />
          <kbd class="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">Enter</kbd>
        </div>
        <p v-if="error" class="mt-2 text-xs text-rose-600">{{ error }}</p>
        <p v-else class="mt-2 text-xs text-slate-400">
          Enter opens the typed ID or the highlighted game. Ctrl/Cmd + Enter opens it in a new tab. Use the arrow keys to move through the list.
        </p>
      </form>

      <!-- Recently added games, one creation day at a time -->
      <div class="border-t border-slate-100">
        <div class="flex items-center justify-between px-5 pt-3 pb-1.5">
          <span class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Recently added</span>
          <span v-if="allFixtures.length" class="text-[11px] text-slate-400">
            {{ visibleFixtures.length }} of {{ allFixtures.length }} loaded
          </span>
        </div>

        <div ref="listRef" class="max-h-[48vh] overflow-y-auto pb-3">
          <p v-if="loadError" class="px-5 py-4 text-xs text-rose-600">{{ loadError }}</p>
          <p v-else-if="!loading && days.length === 0" class="px-5 py-4 text-xs text-slate-400">No fixtures found.</p>
          <p v-else-if="!loading && visibleFixtures.length === 0" class="px-5 py-4 text-xs text-slate-400">
            No loaded game starts with #{{ fixtureId }}. Press Enter to open it anyway, or load more days below.
          </p>

          <template v-for="day in visibleDays" :key="day.day">
            <div class="sticky top-0 z-10 flex items-center justify-between border-y border-slate-100 bg-slate-50/95 px-5 py-1.5 backdrop-blur">
              <span class="text-[11px] font-semibold text-slate-600">Added {{ formatDay(day.day) }}</span>
              <span class="text-[11px] text-slate-400">{{ day.rows.length }} {{ day.rows.length === 1 ? 'game' : 'games' }}</span>
            </div>
            <ul>
              <li
                v-for="row in day.rows"
                :key="row.fixture.id"
                :data-index="row.index"
                class="flex cursor-pointer items-center gap-3 px-5 py-2 text-sm transition-colors"
                :class="row.index === selectedIndex ? 'bg-blue-50 shadow-[inset_3px_0_0_0_#0ea5e9]' : 'hover:bg-slate-50'"
                :title="'Click to open, Ctrl/Cmd + Click for new tab'"
                @mouseenter="selectedIndex = row.index"
                @click="openFixture(row.fixture.id, $event.ctrlKey || $event.metaKey)"
                @auxclick.prevent="$event.button === 1 && openFixture(row.fixture.id, true)"
              >
                <span class="w-14 shrink-0 font-semibold tabular-nums text-rose-600">#{{ row.fixture.id }}</span>
                <span class="min-w-0 flex-1 truncate">
                  <span class="font-medium text-slate-900">{{ teamName(row.fixture.home_team) }}</span>
                  <span class="mx-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">vs</span>
                  <span class="font-medium text-slate-900">{{ teamName(row.fixture.away_team) }}</span>
                </span>
                <span class="hidden max-w-[10rem] shrink-0 truncate text-xs text-slate-500 sm:inline">
                  {{ row.fixture.league?.league_name || row.fixture.league?.name || '' }}
                </span>
                <span
                  v-if="row.fixture.user?.name"
                  class="hidden max-w-[6rem] shrink-0 truncate rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 md:inline"
                  :title="'Added by ' + row.fixture.user.name"
                >
                  {{ row.fixture.user.name }}
                </span>
                <!-- Single lifecycle status: not published → published → closed → settled -->
                <span
                  class="w-[4.5rem] shrink-0 rounded-full px-1.5 py-0.5 text-center text-[10px] font-semibold uppercase tracking-wide"
                  :class="fixtureStatus(row.fixture).class"
                  :title="fixtureStatus(row.fixture).title"
                >
                  {{ fixtureStatus(row.fixture).label }}
                </span>
                <span class="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-slate-700">
                  {{ formatGamedate(row.fixture.gamedate) }}
                </span>
              </li>
            </ul>
          </template>

          <div class="px-5 pt-3">
            <button
              type="button"
              class="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-default disabled:opacity-60"
              :disabled="loading || !hasMore"
              @click="loadMore"
            >
              <template v-if="loading">Loading…</template>
              <template v-else-if="hasMore">Load previous day</template>
              <template v-else>No earlier games</template>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from '../../axios.js'

const emit = defineEmits(['close'])

const router = useRouter()
const inputRef = ref(null)
const listRef = ref(null)
const fixtureId = ref('')
const error = ref('')

// Loaded creation days, newest first: [{ day: 'YYYY-MM-DD', fixtures: [...] }]
const days = ref([])
const loading = ref(false)
const loadError = ref('')
const hasMore = ref(true)
const selectedIndex = ref(-1)

const allFixtures = computed(() => days.value.flatMap(d => d.fixtures))

// Typing an ID narrows every loaded day to matching IDs; rows get a flat index for keyboard navigation.
const visibleDays = computed(() => {
  const prefix = fixtureId.value.trim()
  let index = 0
  return days.value
    .map(d => ({
      day: d.day,
      rows: d.fixtures
        .filter(f => !prefix || String(f.id).startsWith(prefix))
        .map(f => ({ fixture: f, index: index++ }))
    }))
    .filter(d => d.rows.length > 0)
})
const visibleFixtures = computed(() => visibleDays.value.flatMap(d => d.rows.map(r => r.fixture)))

watch(visibleFixtures, (list) => {
  if (selectedIndex.value >= list.length) selectedIndex.value = list.length - 1
})

onMounted(async () => {
  await nextTick()
  inputRef.value?.focus()
  loadMore()
})

// Fetches the next earlier day that had fixtures added. The cursor is the start of the
// oldest loaded day; the server picks the most recent day before it with additions.
const loadMore = async () => {
  if (loading.value || !hasMore.value) return
  loading.value = true
  loadError.value = ''
  const oldest = days.value[days.value.length - 1]?.day
  try {
    const response = await axios.get('/api/fixtures/global-search', {
      params: {
        sort: 'created',
        perDay: 1,
        ...(oldest ? { createdBefore: `${oldest} 00:00:00` } : {})
      }
    })
    const fixtures = Array.isArray(response.data) ? response.data : []
    const day = fixtures[0]?.created_day
    if (!fixtures.length || !day || days.value.some(d => d.day === day)) {
      hasMore.value = false
      return
    }
    days.value = [...days.value, { day, fixtures }]
  } catch (err) {
    console.error('Error loading recently added fixtures:', err)
    loadError.value = 'Could not load recently added games.'
  } finally {
    loading.value = false
  }
}

const close = () => emit('close')

const teamName = (team) => team?.team_name || team?.name || 'Unknown Team'

// Current lifecycle state, mirroring the webhook event sequence
// (fixture.created/published → fixture.closed → fixture.settlement). The fixture row has no
// status column; the state is the latest of its timestamps. "Closed" is auto_suspended_at:
// the game started and the lines were closed, which the fixture.closed webhook sends as closed_at.
const fixtureStatus = (fixture) => {
  if (fixture.settlement_at) {
    return { label: 'Settled', class: 'bg-violet-100 text-violet-700', title: `Settled ${formatGamedate(fixture.settlement_at)}` }
  }
  if (fixture.auto_suspended_at) {
    return { label: 'Closed', class: 'bg-amber-100 text-amber-700', title: `Closed ${formatGamedate(fixture.auto_suspended_at)}` }
  }
  if (fixture.published_at) {
    return { label: 'Published', class: 'bg-emerald-100 text-emerald-700', title: `Published ${formatGamedate(fixture.published_at)}` }
  }
  return { label: 'Draft', class: 'bg-slate-100 text-slate-500', title: 'Not published yet' }
}

const formatGamedate = (gamedate) => {
  if (!gamedate) return ''
  // Accepts "YYYY-MM-DD HH:mm:ss", ISO, or "DD.MM.YYYY HH:mm".
  const [datePart = '', timePart = ''] = String(gamedate).replace('T', ' ').split(' ')
  const time = timePart.substring(0, 5)
  if (datePart.includes('.')) return `${datePart} ${time}`.trim()
  const [y, m, d] = datePart.split('-')
  if (!y || !m || !d) return String(gamedate)
  return `${d}.${m}.${y} ${time}`.trim()
}

// 'YYYY-MM-DD' → 'DD.MM.YYYY', with Today / Yesterday for the two most recent days.
const formatDay = (day) => {
  const [y, m, d] = String(day).split('-')
  if (!y || !m || !d) return String(day)
  const pad = (n) => String(n).padStart(2, '0')
  const today = new Date()
  const todayKey = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  const yesterdayKey = `${yesterday.getFullYear()}-${pad(yesterday.getMonth() + 1)}-${pad(yesterday.getDate())}`
  const formatted = `${d}.${m}.${y}`
  if (day === todayKey) return `today, ${formatted}`
  if (day === yesterdayKey) return `yesterday, ${formatted}`
  return formatted
}

const onInput = () => {
  // Keep only digits so pasting "#5123" or "5123 " still works.
  const digits = fixtureId.value.replace(/\D/g, '')
  if (digits !== fixtureId.value) fixtureId.value = digits
  error.value = ''
  // A fresh ID beats a stale highlight; the arrow keys re-select.
  selectedIndex.value = -1
}

const moveSelection = (delta) => {
  const count = visibleFixtures.value.length
  if (!count) return
  const next = selectedIndex.value < 0
    ? (delta > 0 ? 0 : count - 1)
    : (selectedIndex.value + delta + count) % count
  selectedIndex.value = next
  nextTick(() => {
    listRef.value?.querySelector(`[data-index="${next}"]`)?.scrollIntoView({ block: 'nearest' })
  })
}

const onKeydown = (event) => {
  // Keep the popup's keys away from the global shortcut handler (Ctrl+K etc.).
  event.stopPropagation()
  if (event.key === 'Escape') {
    event.preventDefault()
    close()
    return
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    moveSelection(1)
    return
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault()
    moveSelection(-1)
    return
  }
  if (event.key === 'Enter') {
    event.preventDefault()
    go(event.ctrlKey || event.metaKey)
  }
}

const go = (newTab = false) => {
  const typed = fixtureId.value.trim()
  const highlighted = selectedIndex.value >= 0 ? visibleFixtures.value[selectedIndex.value] : null
  // Typed ID wins; otherwise the highlighted game; otherwise the top of the list.
  const id = typed || highlighted?.id || visibleFixtures.value[0]?.id
  if (!id) {
    error.value = 'Enter a fixture ID.'
    inputRef.value?.focus()
    return
  }
  openFixture(id, newTab)
}

const openFixture = (id, newTab = false) => {
  const target = `/fixture/${id}`
  close()
  if (newTab) {
    window.open(router.resolve(target).href, '_blank')
  } else {
    router.push(target)
  }
}
</script>
